import {CbError} from '@/hosted_fields/common/errors';
import {Gateway} from '@/extensions/three_domain_secure/common/types';

interface URLMap {
  [url: string]: Promise<any>;
}

export default class GatewayScriptLoader {
  private gwLoaded: URLMap;

  constructor() {
    this.gwLoaded = {};
  }

  loadScript(gwConfig): Promise<any> {
    if (!gwConfig) {
      return Promise.reject(new CbError('Missing gateway configuration'));
    }

    if (!gwConfig.tokenization_config) {
      return Promise.resolve(true);
    }

    let data = this._getData(gwConfig);

    if (!this.gwLoaded[data.gwInstanceRef]) {
      this.gwLoaded[data.gwInstanceRef] = this._loadScript(data);
    }

    return this.gwLoaded[data.gwInstanceRef];
  }

  private _loadScript({url, namespace, gwName, stripeKey}): Promise<any> {
    return new Promise((resolve, reject) => {
      this._asyncLoad(
        url,
        namespace,
        () => resolve(true),
        () => reject(`${gwName} js taking a long time to load.`)
      );
    }).then(() => {
      if (namespace == 'Stripe' && stripeKey) {
        // @ts-ignore
        window[namespace].setPublishableKey(stripeKey); // Stripe is initialized here.
      }
      if (namespace == 'braintree') {
        return this._loadBraintreeAdditionalJs(gwName);
      }
    });
  }

  private _loadBraintreeAdditionalJs({gwName}) {
    const b = window.braintree;
    const url = `https://js.braintreegateway.com/web/${b.client.VERSION}/js/data-collector.min.js`;
    return new Promise((resolve, reject) => {
      this._asyncLoad(
        url,
        'dataCollector',
        () => resolve(true),
        () => reject(`${gwName} js taking a long time to load.`),
        b
      );
    });
  }

  private _asyncLoad(url, namespace, onsuccess, onfail, rootNamespace: any = window) {
    if (rootNamespace[namespace]) {
      onsuccess();
    } else {
      let timeout = window.setTimeout(onfail, 50000);
      var newScript = document.createElement('script');
      let onload = () => {
        if (rootNamespace[namespace]) {
          clearTimeout(timeout);
          window.setTimeout(onsuccess, 100);
        } else {
          window.setTimeout(onload, 100);
        }
      };
      newScript.onload = onload;
      document.head.appendChild(newScript);
      newScript.src = url;
    }
  }

  private _getData(gwConfig): any {
    let confKey = Object.keys(gwConfig.tokenization_config).filter((k) => k != 'common_config')[0];
    let tokenConfig = gwConfig.tokenization_config[confKey];

    let out = {
      gwName: tokenConfig.gw_name,
      url: tokenConfig.js_url,
      gwInstanceRef: tokenConfig.js_url,
    };

    switch (tokenConfig.handler) {
      case Gateway.STRIPE:
        out['namespace'] = 'Stripe';
        out['stripeKey'] = tokenConfig.client_id;
        out['gwInstanceRef'] = `${out.url}-${tokenConfig.client_id}`;
        break;
      case Gateway.BRAINTREE:
        out['namespace'] = 'braintree';
        break;
      case Gateway.AUTHORIZE_NET:
        out['namespace'] = 'Accept';
        break;
      case Gateway.ECENTRIC:
      case Gateway.METRICS_GLOBAL:
      case Gateway.WINDCAVE:
      case Gateway.NMI:
      case Gateway.DLOCAL:
      case Gateway.Nuvei:
        out['namespace'] = 'payfurl';
        break;
      case Gateway.EBANX:
        out['namespace'] = 'EBANX';
    }

    return out;
  }
}
